import { BaseElementWithoutShadowDOM } from '@theme/base-element'
import '@theme/ui-product-form'
import '@theme/ui-quantity-input'
import '@theme/ui-variant-picker'
import { VARIANT_PICKER_CHANGE_EVENT } from '@theme/ui-variant-picker'
import HTMLUpdateUtility from '@theme/mixin-html-update-utility'

export class MainProduct extends BaseElementWithoutShadowDOM {
  constructor() {
    super(...arguments)

    this.$htmlUpdateUtility = new HTMLUpdateUtility(this)
    this.placement = 'product-card'

    /**
     * Handles the click event on the product component and sends analytics data to Shopify.
     *
     * This function retrieves product analytics data from a `<script>` tag with `data-analytics`,
     * parses the JSON content, and publishes an `analytics:product_card_click` event using Shopify's
     * analytics API. This allows Shopify's analytics system to track product clicks for reporting
     * and insights.
     */
    this.trackProductClickAnalytics = async (event) => {
      const target = event.target
      const isLinkClick = target.closest('a')

      if (!isLinkClick) {
        return
      }

      const analyticsScript = this.querySelector('script[data-analytics]')

      if (!analyticsScript) {
        console.warn('Analytics script not found')
        return
      }

      const analyticsDataText = analyticsScript.textContent?.trim()

      if (!analyticsDataText) {
        console.warn('Analytics script is empty')
        return
      }

      event.preventDefault()

      const analyticsDataJson = JSON.parse(analyticsDataText)
      await this.sendAnalyticsData(analyticsDataJson)

      window.location.href = isLinkClick.getAttribute('href')
    }
  }
  static get properties() {
    return {
      sectionId: { type: String, attribute: 'section-id' },
      productUrl: { type: String, attribute: 'product-url' },
      variantId: { type: String, attribute: 'variant-id' },
      view: { type: String, attribute: 'view' },
      analytics: { type: Boolean, attribute: 'analytics' },
      placement: { type: String, attribute: 'placement' },
      shouldUpdateUrl: {
        type: String,
        attribute: 'update-url',
        converter: (value) => value === 'true',
      },
    }
  }

  connectedCallback() {
    super.connectedCallback()
    this.handleVariantPickerChange = this.handleVariantPickerChange.bind(this)
    this.handleDesktopScroll()
    this.addEventListener(
      VARIANT_PICKER_CHANGE_EVENT,
      this.handleVariantPickerChange
    )

    if (this.placement === 'pdp') {
      document.addEventListener('cart:updated', () => this.handleCartUpate())
    }

    this.$htmlUpdateUtility.addPostProcessCallback(() => {
      window?.Shopify?.PaymentButton?.init()
    })

    this.$htmlUpdateUtility.addPostProcessCallback((html) =>
      this.refreshTolstoyWidgets(html)
    )

    if (this.analytics) {
      this.addEventListener('click', this.trackProductClickAnalytics)
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener(
      VARIANT_PICKER_CHANGE_EVENT,
      this.handleVariantPickerChange
    )

    this.removeEventListener('click', this.trackProductClickAnalytics)
  }

  // Re-initialise Tolstoy video widgets inside HTML swapped in after a variant change
  refreshTolstoyWidgets(html) {
    html
      .querySelectorAll('tolstoy-stories[data-publish-id]')
      .forEach((element) => {
        const publishId = element.dataset.publishId
        const widget = publishId ? window.tolstoyWidget?.[publishId] : undefined
        if (!widget?.init) return
        try {
          widget.init({
            productId: element.dataset.productId,
            tags: element.dataset.tags,
          })
        } catch (error) {
          console.warn('Tolstoy widget refresh failed', error)
        }
      })
  }

  updateURL(url, variantId) {
    if (!this.shouldUpdateUrl) return
    window.history.replaceState(
      {},
      '',
      `${url}${variantId ? `?variant=${variantId}` : ''}`
    )
  }

  handleDesktopScroll() {
    let buyButtons = this.querySelector('#buy-buttons')
    const footer = document.querySelector('#main-footer')

    if (!buyButtons || !footer) {
      return
    }

    const intersectionOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0,
    }

    let isBuyButtonsVisible = true
    let isFooterVisible = false

    const updateStickyState = () => {
      if (this.view === 'quick-shop') {
        return
      }

      if (!isBuyButtonsVisible && !isFooterVisible) {
        buyButtons.classList.add('sticky-atc')
      } else {
        buyButtons.classList.remove('sticky-atc')
      }
    }

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.target === buyButtons) {
          isBuyButtonsVisible = entry.isIntersecting
        } else if (entry.target === footer) {
          isFooterVisible = entry.isIntersecting
        }
      })
      updateStickyState()
    }

    const observer = new IntersectionObserver(
      observerCallback,
      intersectionOptions
    )

    observer.observe(buyButtons)
    observer.observe(footer)

    // Variant changes re-render the block wrapping #buy-buttons, which creates a new
    // element. Observe the new one so the sticky bar keeps working after the swap.
    this.$htmlUpdateUtility.addPostProcessCallback(() => {
      const nextBuyButtons = this.querySelector('#buy-buttons')
      if (!nextBuyButtons) return

      if (nextBuyButtons !== buyButtons) {
        observer.unobserve(buyButtons)
        buyButtons = nextBuyButtons
        observer.observe(buyButtons)
      }

      // Re-apply current state; the swapped-in markup doesn't carry the sticky-atc class
      updateStickyState()
    })
  }

  handleVariantPickerChange(event) {
    const newProductUrl = event.detail.productUrl
    const targetVariantId = event.detail.variantId

    if (!targetVariantId || targetVariantId.trim() === '') return
    this.updateURL(newProductUrl ?? '', targetVariantId)

    if (
      newProductUrl &&
      newProductUrl !== '' &&
      newProductUrl !== this.productUrl
    ) {
      this.productUrl = newProductUrl
    }

    this.updateProductDetails({
      productUrl: newProductUrl ? newProductUrl : this.productUrl,
      variantId: targetVariantId,
    })
  }

  handleCartUpate() {
    this.updateProductDetails({
      productUrl: this.productUrl,
      variantId: this.variantId,
      withCache: false,
    })
  }

  updateProductDetails({ productUrl, variantId, withCache = true }) {
    this.$htmlUpdateUtility
      .fetchAndReplaceSectionId({
        renderRoot: this,
        sectionId: this.sectionId,
        baseUrl: productUrl,
        params: {
          variant: variantId,
          ...(this.view ? { view: this.view } : {}),
        },
        withCache,
      })
      .catch((error) => {
        if (error instanceof Error) {
          throw error
        } else {
          console.warn(error)
        }
      })
  }

  sendAnalyticsData(data) {
    return new Promise((resolve) => {
      window.Shopify?.analytics?.publish('analytics:product_card_click', data)
      setTimeout(() => {
        resolve()
      }, 1000)
    })
  }
}

customElements.define('main-product', MainProduct)
